import { useEffect, useRef, type KeyboardEvent as ReactKeyboardEvent } from 'react'

import { cn } from '@/ui/lib/utils'
import { useBlink } from '@/ui/useBlink'

export type SignalTag = 'save' | 'warn' | 'err'

export interface SignalLogLine {
  /** 24-hour time, no seconds — e.g. `19:30`. */
  at: string
  tag: SignalTag
  msg: string
}

export interface SignalTerminalProps {
  logs: SignalLogLine[]
  open: boolean
  onToggle: () => void
  /** Controlled command-line value (so the app can clear it after submit). */
  cmd: string
  onCmdChange: (value: string) => void
  /** Fired on Enter with the trimmed command; the app parses clear/hue/warn/err. */
  onCommand: (raw: string) => void
  className?: string
}

/**
 * Severity → text colour (spec 010 §8). `warn` is the fixed AMBER `#CB8A00`
 * (the token layer defines only `--alert`, so warn is a literal by design, not
 * an accent token); `err` is `--alert`; a `save`/write is plain `--paper`.
 */
const SEVERITY: Record<SignalTag, string> = {
  save: 'text-paper',
  warn: 'text-[#CB8A00]',
  err: 'text-alert',
}

/**
 * Station Kit v2's always-present status surface (spec 010 §8), mounted once in
 * the shell. Collapsed it is a 28px bar — a blinking `--accent-line` square,
 * `SIGNAL`, the last line coloured by severity, `WARN n · ERR n`, and an
 * expand box; open it grows a 204px scrolling log plus a command line with an
 * `--accent-line` `>` prompt.
 *
 * **Presentational and controlled** — it renders `logs` and reports intent
 * (`onToggle`, `onCmdChange`, `onCommand`). The log store, the `echo:log`
 * transport, and the command semantics (`clear`, `hue`, `warn`, `err`) are an
 * `src/app/` concern (spec 010 step 6); `src/ui/` may not import app state, so
 * that wiring stays outside this component. It records errors, warnings and
 * writes only — never routine chatter.
 */
export function SignalTerminal({
  logs,
  open,
  onToggle,
  cmd,
  onCmdChange,
  onCommand,
  className,
}: SignalTerminalProps) {
  // 1.6s full cycle → an ~800ms on/off half-phase; stays lit under reduced motion.
  const blink = useBlink(800)
  const scrollRef = useRef<HTMLDivElement>(null)

  const last = logs.length > 0 ? logs[logs.length - 1] : undefined
  const warnCount = logs.filter((line) => line.tag === 'warn').length
  const errCount = logs.filter((line) => line.tag === 'err').length

  // Keep the newest line in view whenever the log grows while expanded.
  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [open, logs])

  function handleCommandKeyDown(event: ReactKeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter') {
      event.preventDefault()
      const raw = cmd.trim()
      if (raw) onCommand(raw)
    }
  }

  return (
    <section
      role="region"
      aria-label="Signal terminal"
      className={cn('flex flex-col border-t border-paper-14 bg-ink text-paper', className)}
    >
      <button
        type="button"
        aria-expanded={open}
        onClick={onToggle}
        className="flex h-7 shrink-0 items-center gap-[14px] px-[14px] text-left transition-colors duration-[90ms] hover:bg-[#141414]"
      >
        <span
          aria-hidden="true"
          className={cn('size-1.5 shrink-0 bg-accent-line', !blink && 'opacity-0')}
        />
        <span className="font-sans text-[10px] tracking-[0.2em] text-paper-40 uppercase">Signal</span>
        <span aria-hidden="true" className="text-paper-40">
          │
        </span>
        <span
          aria-live="polite"
          className={cn('flex-1 truncate font-mono text-[9.5px]', last ? SEVERITY[last.tag] : 'text-paper-40')}
        >
          {last ? last.msg : 'No signal on record.'}
        </span>
        <span className="shrink-0 font-sans text-[10px] tracking-[0.2em] text-paper-40 uppercase">
          Warn {warnCount} · Err {errCount}
        </span>
        <span className="shrink-0 border border-paper-35 px-2 py-0.5 font-mono text-[9.5px] text-paper-40">
          {open ? '[ close ]' : '[ expand ]'}
        </span>
      </button>

      {open && (
        <>
          <div ref={scrollRef} className="h-[204px] overflow-y-auto px-[14px] py-2">
            <ol className="flex flex-col gap-[3px] font-mono text-[11px]">
              {logs.map((line, index) => (
                <li key={index} className="flex gap-2">
                  <span className="w-11 shrink-0 text-paper-40">{line.at}</span>
                  <span className="w-[60px] shrink-0 font-sans text-[10px] font-bold tracking-[0.16em] uppercase">
                    {line.tag}
                  </span>
                  <span className={cn('min-w-0 flex-1', SEVERITY[line.tag])}>{line.msg}</span>
                </li>
              ))}
            </ol>
          </div>
          <div className="flex h-8 shrink-0 items-center gap-2 border-t border-paper-14 px-[14px]">
            <span aria-hidden="true" className="font-mono text-accent-line">
              {'>'}
            </span>
            <input
              value={cmd}
              onChange={(event) => onCmdChange(event.target.value)}
              onKeyDown={handleCommandKeyDown}
              aria-label="Terminal command"
              placeholder="clear · hue 205 · warn … · err …"
              className="min-w-0 flex-1 border-none bg-transparent font-mono text-[11px] text-paper caret-accent-line outline-none placeholder:text-paper-40"
            />
            <span className="shrink-0 font-mono text-[10px] tracking-[0.1em] text-paper-40 uppercase">
              {logs.length} lines
            </span>
          </div>
        </>
      )}
    </section>
  )
}
